import { AiAgentActions } from "../../redux/actions/aiAgentActions";
import { AgentState, ConversationMessageInfo, ConversationSummary } from "../../redux/reducers/aiAgentReducer";
import { createAppError, parseErrorFromResponse, getErrorCodeFromResponse, isTokenExpiredError, getErrorCodeFromMessage, retryWithBackoff } from "../api/errorHandler";
import { HTTPContentType } from "../api/useApi";
import { UserAccount } from "../types";
import { IAgentMetadata, IChatItem, MessageContext } from "../types/aiTypes";
import { AppError, isAppError } from "../types/errors";
import { parseSseLine, splitSseBuffer } from "../utilities/sseParser";
import { convertFilesToDataUris, createAttachmentMetadata } from "./fileAttachments";

export class CopilotService {
    private primaryAccount: UserAccount;
    private agentActions?: AiAgentActions;
    private agentState?: AgentState;

    private currentStreamAbort?: AbortController;
    private streamCancelled = false;

    // Chat state wiring is optional: sendSilentMessage dispatches nothing.
    public constructor(primaryAccount: UserAccount, aiAgentActions?: AiAgentActions, agentState?: AgentState) {
        this.primaryAccount = primaryAccount;
        this.agentActions = aiAgentActions;
        this.agentState = agentState;
    }

    private agentMetadata: IAgentMetadata | undefined;

    public async start(): Promise<void> {
        // Initialize Copilot services here
        const agentResp = await fetch('/api/Copilot/agent', {
            method: 'GET',
            headers: this.getHTTPHeaders(HTTPContentType.json),
        });

        if (!agentResp.ok) {
            console.error('Failed to initialize Copilot services', agentResp.statusText);
        } 

        const data = await agentResp.json();
        //console.log('Fetched agent metadata:', data);
        this.agentMetadata = data;
    }

    public getAgentMetadata(): IAgentMetadata | undefined {
        return this.agentMetadata;
    }

    private getHTTPHeaders(contentType?: string): Record<string, string> {
        const headers: Record<string, string> = { Accept: contentType || HTTPContentType.json };
        if (contentType) headers['Content-Type'] = contentType;
        return headers;
    }

    /**
     * Prepare message payload with optional file attachments.
     * Converts files to data URIs and separates images from documents.
     *
     * @param text - Message text content
     * @param files - Optional array of files (images and documents)
     * @returns Payload with content, image URIs, file attachments, and attachment metadata
     */
    private async prepareMessagePayload(
        text: string,
        files?: File[],
    ): Promise<{
        content: string;
        imageDataUris: string[];
        fileDataUris: Array<{ dataUri: string; fileName: string; mimeType: string }>;
        attachments: IChatItem['attachments'];
    }> {
        let imageDataUris: string[] = [];
        let fileDataUris: Array<{ dataUri: string; fileName: string; mimeType: string }> = [];
        let attachments: IChatItem['attachments'] = undefined;

        if (files && files.length > 0) {
            try {
                const results = await convertFilesToDataUris(files);

                // Separate images from documents
                const imageResults = results.filter((r) => r.mimeType.startsWith('image/'));
                const fileResults = results.filter((r) => !r.mimeType.startsWith('image/'));

                imageDataUris = imageResults.map((r) => r.dataUri);
                fileDataUris = fileResults.map((r) => ({
                    dataUri: r.dataUri,
                    fileName: r.name,
                    mimeType: r.mimeType,
                }));

                // Create attachment metadata for UI display
                attachments = createAttachmentMetadata(results);
            } catch (error) {
                const appError = createAppError(error);
                this.agentActions?.chatError(appError);
                throw appError;
            }
        }

        return { content: text, imageDataUris, fileDataUris, attachments };
    }

    /**
     * Construct request body for chat API.
     *
     * @param message - User message text
     * @param conversationId - Current conversation ID (null for new conversations)
     * @param imageDataUris - Array of base64 data URIs for images
     * @param fileDataUris - Array of file attachments with metadata
     * @returns Request body object
     */
    private constructRequestBody(
        message: string,
        conversationId: string | null,
        imageDataUris: string[],
        fileDataUris: Array<{ dataUri: string; fileName: string; mimeType: string }>,
        context?: MessageContext,
    ): Record<string, unknown> {

        const cachedContext = this.agentState?.context;
        const effectiveContext: MessageContext = {
            ...cachedContext,
            ...context,
            accountId: this.primaryAccount.account.accountId,
        };
        const contextChanged = JSON.stringify(effectiveContext) !== JSON.stringify(cachedContext);
        if (contextChanged) {
            this.agentActions?.contextSetContext(effectiveContext);
        }

        // Always send context on every turn: the server issues a new SessionDoc per
        // request and does not remember prior context, so omitting it makes accountId
        // land in Cosmos as null on the 2nd+ message.
        return {
            message,
            conversationId,
            imageDataUris: imageDataUris.length > 0 ? imageDataUris : undefined,
            fileDataUris: fileDataUris.length > 0 ? fileDataUris : undefined,
            context: effectiveContext,
        };
    }

    /**
     * Initiate streaming fetch request to chat API.
     * Validates response and throws typed errors on failure.
     *
     * @param url - API endpoint URL
     * @param token - Access token
     * @param body - Request body
     * @param signal - Abort signal for cancellation
     * @returns Response object
     * @throws {AppError} If request fails or response is not OK
     */
    private async initiateStream(
        url: string,
        body: Record<string, unknown>,
        signal: AbortSignal,
    ): Promise<Response> {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal,
        });

        if (!res.ok) {
            const errorMessage = await parseErrorFromResponse(res);
            const errorCode = getErrorCodeFromResponse(res);
            throw createAppError(new Error(errorMessage), errorCode);
        }

        return res;
    }

    /**
     * Send a message and stream the response from the Azure AI Agent.
     * Orchestrates authentication, file conversion, optimistic UI updates, and streaming.
     * Retries the full stream cycle up to 3 times on retryable errors, then recovers
     * the message text back to the input if all retries fail.
     *
     * @param messageText - The user's message text
     * @param currentConversationId - Current conversation ID (null for new conversations)
     * @param files - Optional array of files to attach (images and documents)
     * @throws {Error} If authentication fails (non-retryable)
     */
    public async sendMessage(
        messageText: string,
        currentConversationId: string | null,
        files?: File[],
        context?: MessageContext,
    ): Promise<void> {
        if (this.currentStreamAbort) {
            this.streamCancelled = true;
            this.currentStreamAbort.abort();
            this.agentActions?.chatCancelStream();
        }
        
        const { content, imageDataUris, fileDataUris, attachments } =
            await this.prepareMessagePayload(messageText, files);

        const userMessage: IChatItem = {
            id: Date.now().toString(),
            role: 'user',
            content,
            attachments,
            more: {
                time: new Date().toISOString(),
            },
        };

        this.agentActions?.chatSendMessage(userMessage);

        const assistantMessageId = (Date.now() + 1).toString();
        this.agentActions?.chatAddAssistantMessage(assistantMessageId);
        this.agentActions?.chatStartStream(
            assistantMessageId,
            currentConversationId || undefined,
        );

        const requestBody = this.constructRequestBody(
            messageText,
            currentConversationId,
            imageDataUris,
            fileDataUris,
            context,
        );

        const maxRetries = 3;
        let lastError: unknown;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                if (attempt > 1) {
                    this.agentActions?.chatStreamRetry(assistantMessageId, attempt, maxRetries);
                    await new Promise((resolve) =>
                        setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)),
                    );
                }

                this.currentStreamAbort = new AbortController();
                this.streamCancelled = false;

                const response = await this.initiateStream(
                    '/api/copilot/chat/stream',
                    requestBody,
                    this.currentStreamAbort.signal,
                );

                await this.processStream(response, assistantMessageId, currentConversationId);
                this.currentStreamAbort = undefined;
                this.streamCancelled = false;
                return;
            } catch (error) {
                lastError = error;
                this.currentStreamAbort = undefined;
                this.streamCancelled = false;

                // User cancelled
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }

                if (isAppError(error) && error.code === 'AUTH') {
                    this.agentActions?.chatError(error);
                    throw error;
                }

                if (attempt === maxRetries) {
                    break;
                }
            }
        }

        const appError: AppError = isAppError(lastError)
            ? lastError
            : createAppError(lastError, getErrorCodeFromMessage(lastError));

        this.agentActions?.chatRecoverMessage(messageText, appError, maxRetries);
    }

    /**
     * Send a one-off message to the agent without touching chat UI state.
     * Nothing is dispatched to Redux, and the user's active chat stream is left alone.
     * Always starts a fresh conversation so the exchange stays out of the current thread.
     *
     * @param messageText - The prompt to send
     * @param files - Optional files to attach (images and documents)
     * @param context - Message context; accountId is required for AI credit accounting
     * @returns The agent's full response text
     * @throws {AppError} If conversion, the request, or the stream fails
     */
    public async sendSilentMessage(
        messageText: string,
        files?: File[],
        context?: MessageContext,
    ): Promise<string> {
        const results = files?.length ? await convertFilesToDataUris(files) : [];

        const imageDataUris = results.filter((r) => r.mimeType.startsWith('image/')).map((r) => r.dataUri);
        const fileDataUris = results
            .filter((r) => !r.mimeType.startsWith('image/'))
            .map((r) => ({ dataUri: r.dataUri, fileName: r.name, mimeType: r.mimeType }));

        const requestBody = {
            message: messageText,
            conversationId: null,
            imageDataUris: imageDataUris.length > 0 ? imageDataUris : undefined,
            fileDataUris: fileDataUris.length > 0 ? fileDataUris : undefined,
            context,
        };

        // Local controller so cancelling this never aborts the user's chat stream.
        const abort = new AbortController();
        const response = await this.initiateStream('/api/copilot/chat/stream', requestBody, abort.signal);

        return this.collectStream(response);
    }

    /**
     * Read an SSE stream and accumulate its text chunks into a single string.
     * Non-text events (conversationId, annotations, toolUse, usage) are ignored.
     *
     * @param response - Fetch Response object with SSE stream
     * @returns The concatenated text content
     * @throws {AppError} If the stream is not readable or reports an error
     */
    private async collectStream(response: Response): Promise<string> {
        const reader = response.body?.getReader();

        if (!reader) {
            throw createAppError(new Error('Response body is not readable'), 'STREAM');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let text = '';

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const [lines, remaining] = splitSseBuffer(buffer);
                buffer = remaining;

                for (const line of lines) {
                    const event = parseSseLine(line);
                    if (!event) continue;

                    if (event.type === 'chunk') {
                        text += event.data.content ?? '';
                    } else if (event.type === 'done') {
                        return text;
                    } else if (event.type === 'error' || event.data?.error) {
                        throw createAppError(
                            new Error(event.data?.message || event.data?.error?.message || 'Stream error occurred'),
                            'STREAM',
                        );
                    }
                }
            }
        } finally {
            try {
                reader.releaseLock();
            } catch {
                // Reader may already be released
            }
        }

        return text;
    }

    /**
     * Process Server-Sent Events stream from the API.
     * Implements duplicate chunk suppression to prevent UI flicker.
     *
     * @param response - Fetch Response object with SSE stream
     * @param messageId - ID of the assistant message being streamed
     * @param currentConversationId - Current conversation ID (null for new conversations)
     * @throws {Error} If stream is not readable or parsing fails
     */
    private async processStream(
        response: Response,
        messageId: string,
        currentConversationId: string | null,
    ): Promise<void> {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
            const error = createAppError(
                new Error(`Response body is not readable for message ${messageId}`),
                'STREAM',
            );
            this.agentActions?.chatError(error);
            throw error;
        }

        let newConversationId = currentConversationId;
        let lastChunkContent: string | undefined;
        let buffer = '';

        try {
            while (true) {
                if (this.streamCancelled) {
                    break;
                }

                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                const [lines, remaining] = splitSseBuffer(buffer);
                buffer = remaining;

                for (const line of lines) {
                    const event = parseSseLine(line);
                    if (!event) continue;

                    if (event.data?.error) {
                        console.error('[ChatService] SSE error event received:', event.data.error);
                        const error = createAppError(
                            new Error(
                                event.data.error.message ||
                                    event.data.error ||
                                    'Stream error occurred',
                            ),
                            'STREAM',
                        );
                        this.agentActions?.chatError(error);
                        throw error;
                    }

                    switch (event.type) {
                        case 'conversationId':
                            if (!newConversationId) {
                                newConversationId = event.data.conversationId;
                                this.agentActions?.chatStartStream(
                                    messageId,
                                    newConversationId!,
                                );
                            }
                            break;

                        case 'chunk':
                            if (event.data.content !== lastChunkContent) {
                                this.agentActions?.chatStreamChunk(messageId, event.data.content);
                                lastChunkContent = event.data.content;
                            }
                            break;

                        case 'annotations':
                            if (event.data.annotations && event.data.annotations.length > 0) {
                                this.agentActions?.chatStreamAnnotations(
                                    messageId,
                                    event.data.annotations,
                                );
                            }
                            break;

                        case 'toolUse':
                            if (event.data.toolName) {
                                this.agentActions?.chatStreamToolUse(
                                    messageId,
                                    event.data.toolName,
                                );
                            }
                            break;

                        case 'mcpApprovalRequest':
                            if (event.data.approvalRequest) {
                                this.agentActions?.chatMCPApprovalRequest(
                                    messageId,
                                    event.data.approvalRequest,
                                    event.data.approvalRequest.previousResponseId ?? '',
                                );
                            }
                            break;

                        case 'usage':
                            this.agentActions?.chatStreamComplete({
                                promptTokens: event.data.promptTokens,
                                completionTokens: event.data.completionTokens,
                                totalTokens: event.data.totalTokens,
                                duration: event.data.duration,
                            });
                            break;

                        case 'done':
                            return;

                        case 'error': {
                            const error = createAppError(
                                new Error(
                                    `Stream error for message ${messageId}: ${event.data.message}`,
                                ),
                                'STREAM',
                            );
                            this.agentActions?.chatError(error);
                            throw error;
                        }
                    }
                }
            }
        } catch (error) {
            if (
                error instanceof DOMException &&
                error.name === 'AbortError' &&
                this.streamCancelled
            ) {
                // User intentionally cancelled the stream - not an error condition
                return;
            }

            const appError =
                error instanceof Error && 'code' in error
                    ? error
                    : createAppError(
                          new Error(
                              `Stream processing failed: ${error instanceof Error ? error.message : String(error)} (Conversation: ${currentConversationId}, Message: ${messageId})`,
                          ),
                          'STREAM',
                      );
            this.agentActions?.chatError(appError as AppError);
            throw error;
        } finally {
            try {
                reader.releaseLock();
            } catch {
                // Reader may already be released
            }
        }
    }

    /**
     * Send approval response for an MCP tool call.
     *
     * @param approvalRequestId - ID of the approval request
     * @param approved - Whether the tool call was approved
     * @param previousResponseId - Response ID to continue from
     * @param conversationId - Current conversation ID
     */
    async sendMcpApproval(
        approvalRequestId: string,
        approved: boolean,
        previousResponseId: string,
        conversationId: string,
    ): Promise<void> {
        try {
            //const token = await this.ensureAuthToken();

            const assistantMessageId = Date.now().toString();
            this.agentActions?.chatAddAssistantMessage(assistantMessageId);
            this.agentActions?.chatStartStream(assistantMessageId, conversationId);

            this.currentStreamAbort = new AbortController();
            this.streamCancelled = false;

            const requestBody = {
                message: approved ? 'Approved' : 'Rejected',
                conversationId,
                previousResponseId,
                mcpApproval: {
                    approvalRequestId,
                    approved,
                },
            };

            const response = await retryWithBackoff(
                async () =>
                    this.initiateStream(
                        '/api/copilot/chat/stream',
                        //token,
                        requestBody,
                        this.currentStreamAbort!.signal,
                    ),
                3,
                1000,
            );

            await this.processStream(response, assistantMessageId, conversationId);
            this.currentStreamAbort = undefined;
            this.streamCancelled = false;
        } catch (error) {
            this.currentStreamAbort = undefined;
            this.streamCancelled = false;

            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            // trackException(error instanceof Error ? error : new Error(String(error)), {
            //     context: 'sendMcpApproval',
            // });

            const appError: AppError = isAppError(error)
                ? error
                : createAppError(error, getErrorCodeFromMessage(error));

            this.agentActions?.chatError(appError);
            throw error;
        }
    }

    /**
     * Clear chat history and reset to empty state.
     * Dispatches CHAT_CLEAR action to remove all messages and conversation ID.
     */
    public clearChat(): void {
        this.agentActions?.chatClear();
    }

    /**
     * Clear current error state without affecting chat history.
     * Dispatches CHAT_CLEAR_ERROR action.
     */
    public clearError(): void {
        this.agentActions?.chatClearError();
    }

    /**
     * Cancel the current streaming response if any is active.
     * Abort controller is not cleared immediately to allow processStream
     * to observe the cancellation flag and exit gracefully.
     */
    public cancelStream(): void {
        if (this.currentStreamAbort) {
            this.streamCancelled = true;
            this.currentStreamAbort.abort();
            this.agentActions?.chatCancelStream();
        }
    }

    public async downloadFile(
        fileId: string,
        fileName?: string,
        containerId?: string,
    ): Promise<void> {
        //const token = await this.ensureAuthToken();
        const params = containerId ? `?containerId=${encodeURIComponent(containerId)}` : '';
        const response = await fetch(`/api/copilot/files/${encodeURIComponent(fileId)}${params}`, {
            //headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`File download failed: ${response.status}`);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || fileId;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);
    }

    /**
     * List all conversations from the server.
     * @returns Array of conversation summaries
     */
    public async listConversations(
        limit: number = 20,
    ): Promise<{ conversations: ConversationSummary[]; hasMore: boolean }> {
        //const token = await this.ensureAuthToken();
        const accountId = encodeURIComponent(this.primaryAccount.account.accountId);
        const response = await fetch(`/api/copilot/conversations?accountId=${accountId}&limit=${limit}`, {
            // headers: {
            //     Authorization: `Bearer ${token}`,
            // },
        });

        if (!response.ok) {
            throw createAppError(
                new Error(`Failed to list conversations: ${response.status}`),
                'API',
            );
        }

        return response.json();
    }

    /**
     * Get messages for a specific conversation.
     * @param conversationId - The conversation ID to fetch messages for
     * @returns Array of conversation messages
     */
    public async getConversationMessages(
        conversationId: string,
    ): Promise<ConversationMessageInfo[]> {
        //const token = await this.ensureAuthToken();
        const accountId = encodeURIComponent(this.primaryAccount.account.accountId);
        const response = await fetch(`/api/copilot/conversations/${conversationId}/messages?accountId=${accountId}`, {
            // headers: {
            //     Authorization: `Bearer ${token}`,
            // },
        });

        if (!response.ok) {
            throw createAppError(
                new Error(`Failed to get conversation messages: ${response.status}`),
                'API',
            );
        }

        return response.json();
    }

    /**
     * Delete a conversation.
     * @param conversationId - The conversation ID to delete
     */
    public async deleteConversation(conversationId: string): Promise<void> {
        //const token = await this.ensureAuthToken();
        const accountId = encodeURIComponent(this.primaryAccount.account.accountId);
        const response = await fetch(`/api/copilot/conversations/${conversationId}?accountId=${accountId}`, {
            method: 'DELETE',
            // headers: {
            //     Authorization: `Bearer ${token}`,
            // },
        });

        if (!response.ok) {
            throw createAppError(
                new Error(`Failed to delete conversation: ${response.status}`),
                'API',
            );
        }
    }
}